import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { AnnotatedStep, AudienceCard, ButtonLink, Card, Container, CostLine, FaqItem, GradeStamp, Screenshot, Seal, SectionLabel } from '../../design';
import { pagePricing, priced, pricingText } from '../../lib/pricing';
import { roughLandedRange, usd, usdRange } from '../../lib/roughLanded';
import { AnalyticsEvent, ctaEvent } from '../../utils/analytics';
import { blogPath } from '../Blog/posts';
import { CtaBand } from './CtaBand';
import { MountainRoad, PaperworkHanko, Spot } from './illustrations';
import { Journey } from './Journey';
import { MKT, OFFICIAL } from './links';
import { MarketingLayout } from './MarketingLayout';
import { ProductHero } from './ProductParts';
import { CROPS, SHOTS_NOTE } from './shots';
import { TrustGrid } from './TrustGrid';
import './product-shared.css';
import './import-my-own.css';
// /import-my-own: for one person bringing in a JDM car or truck of their own, most likely for
// the first time. Plain words; every claim checked against the app (the individual setup
// guide, Help → Your plan, Customs) — and where a question is legal or regulatory, the answer
// sends them to their broker, state DMV and the official CBP / EPA / NHTSA pages instead.
/** The illustration: JDM cars from an everyday $4,000 van to a $25,000 sports car; live pass price. */ const HAMMER_LOW = 4000;
const HAMMER_HIGH = 25000;
const GRADES = [
    [
        '4.5',
        'Excellent: low miles for its age, only the smallest marks.'
    ],
    [
        '4',
        'Good: minor scratches or dents a detailer can deal with.'
    ],
    [
        '3.5',
        'Average: visible marks, often higher miles. Budget for some work.'
    ],
    [
        '3',
        'Below average: obvious wear or damage, or very high miles.'
    ],
    [
        'R',
        'Repair history: it has had structural repair. Read the sheet closely.'
    ]
];
export default function ImportMyOwnPage() {
    const pricing = pagePricing();
    const t = pricingText(pricing);
    const rough = roughLandedRange({
        hammerLow: HAMMER_LOW,
        hammerHigh: HAMMER_HIGH,
        passCents: pricing.pass.priceCents
    });
    const steps = [
        {
            k: '壱',
            title: 'Open an exporter account',
            text: 'Japan Car Direct or Pacific Coast JDM buys the car at auction for you. The setup guide links their sign-up and has you send the deposit before you find a car, because a bank transfer takes days and exporters won’t take a bid until it lands.'
        },
        {
            k: '弐',
            title: 'Tell it the car you want',
            text: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "Pick the make, model, years and grade. You’ll see what each grade means and how many lots match right now. ",
                    /*#__PURE__*/ _jsx("span", {
                        ...priced('pass.hunts'),
                        children: t['pass.hunts']
                    }),
                    " then search the auctions for you, free. We check on a schedule, as often as every 3 hours, and email you when a match turns up."
                ]
            })
        },
        {
            k: '参',
            title: 'Read the auction sheet',
            text: 'You get each lot’s Japanese inspection sheet in plain English, with the grades, mileage, damage, the inspector’s notes and the frame number. Each one ends in a call of Bid, Caution or Avoid.'
        },
        {
            k: '肆',
            title: 'Bid with a number you can trust',
            text: 'The bid range comes from what the same model sold for, in the same generation and grade, and each sale is linked. Your bid goes to your exporter as an email from your own mailbox. You see it before it goes.'
        },
        {
            k: '伍',
            title: 'Clear customs with a licensed broker',
            text: 'Kappa Customs Brokers is built in, or you can use your own. With Kappa, you sign their Power of Attorney in the app. Whichever broker you pick, the app prepares the ISF, drafts every email to them and warns you before the ISF deadline.'
        },
        {
            k: '陸',
            title: 'Get it home from the port',
            text: 'Once customs releases it, book a carrier. Email a dispatcher, or put quotes from uShip and Super Dispatch side by side.'
        },
        {
            k: '漆',
            title: 'Keep every paper, and the real total',
            text: 'Invoices, the bill of lading, the customs entry and title receipts all live in one place. Each one is read into your landed cost.'
        }
    ];
    const faq = [
        [
            'Do I need a dealer license?',
            /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "Not for Right Hand DMS. Import my own is for bringing in a car for yourself, in your own name or through your LLC. Whether you need a license to import, register or later sell one isn’t ours to say. Ask your ",
                    /*#__PURE__*/ _jsx("a", {
                        href: OFFICIAL.dmv.href,
                        children: "state DMV or title office"
                    }),
                    ", your customs broker and your exporter, and see the official sources below."
                ]
            })
        ],
        [
            'Can I register and drive it on the road?',
            /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "Getting a car into the country is one question. Getting it titled and on the road is another. Ask your ",
                    /*#__PURE__*/ _jsx("a", {
                        href: OFFICIAL.dmv.href,
                        children: "state DMV or title office"
                    }),
                    " what it needs before you bid. For the federal side, see ",
                    /*#__PURE__*/ _jsx("a", {
                        href: OFFICIAL.cbp.href,
                        children: "CBP"
                    }),
                    ",",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: OFFICIAL.epa.href,
                        children: "EPA"
                    }),
                    " and ",
                    /*#__PURE__*/ _jsx("a", {
                        href: OFFICIAL.nhtsa.href,
                        children: "NHTSA"
                    }),
                    "."
                ]
            })
        ],
        [
            'How long does it take?',
            'It depends on the auction calendar, your exporter’s shipping schedule, the ship and the port, so we don’t promise a date. Ask your exporter how long they usually take from a win to sailing. The app keeps track of each step, and Today shows what’s next.'
        ],
        [
            'What if I don’t win a car?',
            /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "Searching is included for ",
                    /*#__PURE__*/ _jsx("span", {
                        ...priced('pass.days'),
                        children: t['pass.days']
                    }),
                    " days from sign-up, and again from each car pass. If you haven’t won one, ask for your sign-up back. We suggest giving it ",
                    /*#__PURE__*/ _jsx("span", {
                        ...priced('guarantee.days'),
                        children: t['guarantee.days']
                    }),
                    " days, and you can ask until ",
                    /*#__PURE__*/ _jsx("span", {
                        ...priced('guarantee.claim'),
                        children: t['guarantee.claim']
                    }),
                    " days after that. We refund it minus what you used, at retail prices, which are currently",
                    ' ',
                    /*#__PURE__*/ _jsx("span", {
                        ...priced('guarantee.sheet'),
                        children: t['guarantee.sheet']
                    }),
                    " per auction sheet and",
                    ' ',
                    /*#__PURE__*/ _jsx("span", {
                        ...priced('guarantee.comp'),
                        children: t['guarantee.comp']
                    }),
                    " per bid estimate. Those follow the top-up prices and can change, and the app shows your exact amount before you ask."
                ]
            })
        ],
        [
            'Who files customs?',
            'A licensed customs broker. Kappa Customs Brokers is built in, or you can use your own. You sign their Power of Attorney and they file the ISF and the entry. The app fills in their forms and drafts each email, and you send it. Which duty applies to your vehicle is the broker’s call (it’s currently 2.5% for a car and 25% for a truck), so ask them before you bid.'
        ],
        [
            'Do I buy the car from you?',
            'No. You buy through your own exporter account and pay them directly. Right Hand DMS finds the lots and reads them, writes your bid email, and keeps track of everything once you’ve won.'
        ],
        [
            'What does the car pass cover?',
            /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "Each car you win gets a pass. It covers that car’s customs, US shipping, documents and costs in the app for ",
                    /*#__PURE__*/ _jsx("span", {
                        ...priced('pass.days'),
                        children: t['pass.days']
                    }),
                    " days from the win. Signing up covers your first car. After that it’s",
                    ' ',
                    /*#__PURE__*/ _jsx("span", {
                        ...priced('pass.price'),
                        children: t['pass.price']
                    }),
                    " per car you win."
                ]
            })
        ]
    ];
    return /*#__PURE__*/ _jsx(MarketingLayout, {
        testid: Testids.app.marketing.importersRoot,
        children: /*#__PURE__*/ _jsxs("main", {
            children: [
                /*#__PURE__*/ _jsx(ProductHero, {
                    k: "個",
                    label: "Import my own",
                    title: "Bring home your own JDM car or truck, one step at a time.",
                    watermark: "個",
                    lead: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "Never imported a car before? Right Hand DMS walks you from opening an exporter account to the car sitting in your driveway. It tells you what the auction sheet says and what to bid, then what customs needs and what the whole thing cost. Signing up is",
                            ' ',
                            /*#__PURE__*/ _jsx("span", {
                                ...priced('pass.price'),
                                children: t['pass.price']
                            }),
                            " and covers your first car."
                        ]
                    }),
                    actions: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(ButtonLink, {
                                href: MKT.signupFor('individual'),
                                "data-testid": Testids.app.marketing.importersCta,
                                ...ctaEvent(AnalyticsEvent.ctaSignup, 'import-hero', 'individual'),
                                children: "Start importing my own"
                            }),
                            /*#__PURE__*/ _jsx(ButtonLink, {
                                href: "#costs",
                                variant: "secondary",
                                children: "See what it costs"
                            })
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(Screenshot, {
                        ...CROPS.budget,
                        bare: true,
                        invert: true,
                        eager: true,
                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("b", {
                                    children: "The setup guide’s budget step."
                                }),
                                " ",
                                SHOTS_NOTE
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Spot, {
                    width: 320,
                    children: /*#__PURE__*/ _jsx(MountainRoad, {})
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-own__rule mk-ground--kinari",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        className: "mk-own__rule-inner",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx(SectionLabel, {
                                        k: "規則",
                                        className: "mk-own__label",
                                        children: "The 25-year rule"
                                    }),
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "mk-own__h2",
                                        children: "Why a 1990s Japanese car can come in at all."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-own__prose",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "Japanese-market vehicles weren’t built to US safety and emissions standards. Once a vehicle is 25 years old, counted from its month of manufacture, it’s exempt from those federal standards and can be imported. In 2026 that covers most vehicles from 2001 and earlier. A late-2001 build clears in its 25th anniversary month."
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "The broker files the EPA and DOT forms that declare the exemption. Titling it and getting it on the road is up to your state, so ask your DMV or title office before you bid."
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "mk-own__links",
                                        children: [
                                            /*#__PURE__*/ _jsx("a", {
                                                href: blogPath('25-year-rule-jdm-cars-eligible-2026'),
                                                children: "Which cars qualify in 2026 →"
                                            }),
                                            /*#__PURE__*/ _jsxs("a", {
                                                href: OFFICIAL.cbp.href,
                                                children: [
                                                    OFFICIAL.cbp.name,
                                                    " ↗"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-own__steps",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        children: [
                            /*#__PURE__*/ _jsx(SectionLabel, {
                                k: "手順",
                                className: "mk-own__label",
                                children: "How the app holds your hand"
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mk-own__h2",
                                children: "Seven steps, and the app knows which one you’re on."
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mk-own__sub",
                                children: "It starts with a setup guide written for first-timers. That covers you, your email, your exporter account, the car you want, your budget, customs and shipping. After that, each car shows its next step until it’s home."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mk-own__steps-grid",
                                children: steps.map((s)=>/*#__PURE__*/ _jsx(AnnotatedStep, {
                                        k: s.k,
                                        title: s.title,
                                        children: s.text
                                    }, s.k))
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-own__grades mk-ground--shu",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        className: "mk-own__grades-inner",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-own__grades-copy",
                                children: [
                                    /*#__PURE__*/ _jsx(SectionLabel, {
                                        k: "評価",
                                        className: "mk-own__label",
                                        children: "Auction grades"
                                    }),
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "mk-own__h2",
                                        children: "An inspector sums up the car in one number."
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mk-own__text",
                                        children: "The auction house inspects every lot and gives it an overall grade, plus a letter for the interior (A is as new, C is normal wear). Mileage caps the grade, so a clean car with high miles can still be a 3.5. Wherever you pick a grade, the app explains it. It also reads the damage map and notes behind it for you."
                                    }),
                                    /*#__PURE__*/ _jsx("a", {
                                        href: blogPath('japanese-auction-grades-explained'),
                                        className: "mk-own__more",
                                        children: "Every grade, explained →"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: "mk-own__grade-list",
                                children: GRADES.map(([g, m])=>/*#__PURE__*/ _jsxs("li", {
                                        className: "mk-own__grade",
                                        children: [
                                            /*#__PURE__*/ _jsx(GradeStamp, {
                                                children: g
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: m
                                            })
                                        ]
                                    }, g))
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("section", {
                    id: "costs",
                    className: "mk-own__costs",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        className: "mk-own__costs-inner",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-own__costs-copy",
                                children: [
                                    /*#__PURE__*/ _jsx(SectionLabel, {
                                        k: "算",
                                        className: "mk-own__label",
                                        children: "What it really costs"
                                    }),
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "mk-own__h2",
                                        children: "The auction price is only part of it."
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "mk-own__text",
                                        children: [
                                            "JDM cars sell for very different money: an everyday 1990s van or sedan can go for",
                                            ' ',
                                            usd(HAMMER_LOW),
                                            " at auction, a clean sports car or Land Cruiser for",
                                            ' ',
                                            usd(HAMMER_HIGH),
                                            " or more. This rough illustration covers that spread, with the same typical ranges the app’s budget step uses. Import duty is shown as a range (recently 12.5–25%) because US tariffs on Japanese vehicles change often, and your broker confirms the rate for your car before you bid. Most of the money goes to the people doing the work: your exporter, the shipping line, US Customs, the broker and the trucker. Very little of it comes to us."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mk-own__fine",
                                        children: "Illustration only. These are typical ranges and this isn’t a quote. Duty depends on how your broker classifies the vehicle and on the tariffs in force the day it enters. Fees and freight change all the time, so ask your exporter and broker before you bid."
                                    }),
                                    /*#__PURE__*/ _jsx("a", {
                                        href: blogPath('jdm-import-cost-calculator'),
                                        className: "mk-own__more",
                                        children: "Try the import cost calculator →"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Card, {
                                shadow: "md",
                                className: "mk-own__ledger",
                                "data-testid": Testids.app.marketing.importersCosts,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mk-own__ledger-head",
                                        children: /*#__PURE__*/ _jsxs("span", {
                                            className: "rhd-caps",
                                            children: [
                                                "Illustration · a car at ",
                                                usd(HAMMER_LOW),
                                                "–",
                                                usd(HAMMER_HIGH)
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mk-own__ledger-lines",
                                        children: rough.lines.map((l)=>/*#__PURE__*/ _jsx(CostLine, {
                                                label: l.label,
                                                amount: usdRange(l.low, l.high),
                                                basis: l.note
                                            }, l.label))
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mk-own__ledger-total",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "Landed, roughly"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: usdRange(rough.low, rough.high)
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-own__price",
                    children: /*#__PURE__*/ _jsx(Container, {
                        children: /*#__PURE__*/ _jsxs(Card, {
                            shadow: "lg",
                            className: "mk-own__price-card",
                            children: [
                                /*#__PURE__*/ _jsx(Seal, {
                                    size: 84,
                                    fontSize: 26,
                                    border: 2.5,
                                    children: "保証"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-own__price-copy",
                                    children: [
                                        /*#__PURE__*/ _jsxs("h2", {
                                            className: "mk-own__price-title",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.price'),
                                                    children: t['pass.price']
                                                }),
                                                " to sign up. Your first car included."
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mk-own__text",
                                            children: [
                                                "Searching is included for ",
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.days'),
                                                    children: t['pass.days']
                                                }),
                                                ' ',
                                                "days from sign-up, and again from each car pass.",
                                                ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.hunts'),
                                                    children: t['pass.hunts']
                                                }),
                                                " at a time, with",
                                                ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.sheets'),
                                                    children: t['pass.sheets']
                                                }),
                                                " auction sheets and",
                                                ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.comps'),
                                                    children: t['pass.comps']
                                                }),
                                                " bid estimates every",
                                                ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.days'),
                                                    children: t['pass.days']
                                                }),
                                                " days. After your first car,",
                                                ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('pass.price'),
                                                    children: t['pass.price']
                                                }),
                                                " per car you win."
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mk-own__text",
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "The guarantee:"
                                                }),
                                                " if you haven’t won a car, we refund your sign-up minus what you used. Usage is charged at retail prices, which follow the top-up packs and can change. We suggest giving it",
                                                ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    ...priced('guarantee.days'),
                                                    children: t['guarantee.days']
                                                }),
                                                " days first. The app shows your exact amount before you ask."
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-own__price-actions",
                                    children: [
                                        /*#__PURE__*/ _jsx(ButtonLink, {
                                            href: MKT.signupFor('individual'),
                                            ...ctaEvent(AnalyticsEvent.ctaSignup, 'import-price', 'individual'),
                                            children: "Start importing my own"
                                        }),
                                        /*#__PURE__*/ _jsx("a", {
                                            href: MKT.pricingFor('individual'),
                                            className: "mk-own__more",
                                            children: "The full terms →"
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Spot, {
                    width: 260,
                    children: /*#__PURE__*/ _jsx(PaperworkHanko, {})
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-own__journey mk-ground--sora",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        children: [
                            /*#__PURE__*/ _jsx(SectionLabel, {
                                k: "道",
                                className: "mk-own__label",
                                children: "The whole trip"
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mk-own__h2",
                                children: "Five stops, same as the dealers make."
                            }),
                            /*#__PURE__*/ _jsx(Journey, {}),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-own__shots",
                                children: [
                                    /*#__PURE__*/ _jsx(Screenshot, {
                                        ...CROPS.search,
                                        bare: true,
                                        invert: true,
                                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "Your hunt’s results,"
                                                }),
                                                " each lot read and scored."
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Screenshot, {
                                        ...CROPS.costs,
                                        bare: true,
                                        invert: true,
                                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "Your car’s landed cost,"
                                                }),
                                                " line by line."
                                            ]
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mk-own__fine",
                                children: SHOTS_NOTE
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-own__faq",
                    "data-testid": Testids.app.marketing.importersFaq,
                    children: /*#__PURE__*/ _jsxs(Container, {
                        className: "mk-own__faq-inner",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mk-own__h2",
                                children: "First-timer questions"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-own__faq-list",
                                children: [
                                    faq.map(([q, a])=>/*#__PURE__*/ _jsx(FaqItem, {
                                            size: "lg",
                                            q: q,
                                            children: a
                                        }, q)),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mk-own__official",
                                        "data-testid": Testids.app.marketing.importersOfficial,
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "mk-own__official-title",
                                                children: "Where to check the rules"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "mk-own__fine",
                                                children: "The government makes these rules. Start with your state’s motor vehicle office, your customs broker and these official pages:"
                                            }),
                                            /*#__PURE__*/ _jsx("ul", {
                                                className: "mk-own__official-list",
                                                children: Object.values(OFFICIAL).map((o)=>/*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsxs("a", {
                                                            href: o.href,
                                                            rel: "noopener",
                                                            children: [
                                                                o.name,
                                                                " ↗"
                                                            ]
                                                        })
                                                    }, o.href))
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(TrustGrid, {
                    title: "Your accounts, your mailbox, your say."
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-own__other",
                    children: /*#__PURE__*/ _jsx(Container, {
                        children: /*#__PURE__*/ _jsx(AudienceCard, {
                            k: "商",
                            who: "Buying to sell?",
                            title: "Running a dealership instead?",
                            promise: "Dealership plans add a hunt for each customer, digests sent from your mailbox, seats for your team and profit per car.",
                            action: /*#__PURE__*/ _jsx(ButtonLink, {
                                href: MKT.forDealers,
                                variant: "secondary",
                                ...ctaEvent(AnalyticsEvent.ctaAudience, 'import-other', 'dealer'),
                                children: "See it for dealers"
                            }),
                            aside: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "Plans from ",
                                    /*#__PURE__*/ _jsx("span", {
                                        ...priced('from'),
                                        children: t.from
                                    }),
                                    " a month"
                                ]
                            })
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(CtaBand, {
                    audience: "individual"
                })
            ]
        })
    });
}
